<template>
	<v-container fluid v-cloak>
		<Contratos
			class="pa-0"
			v-if="$route.query.codigoContrato"
			:key="loading"
			:codigoContrato="$route.query.codigoContrato"
		/>

		<div class="d-flex mt-4">
			<v-btn small to="/contratos" color="primary">
				<v-icon small left>mdi-arrow-left</v-icon>
				Volver a contratos
			</v-btn>
			<v-btn
				class="ml-1"
				small
				@click="
					getIncidencias();
				"
				color="primary"
			>
				<v-icon small left>mdi-reload</v-icon>
				Recargar
			</v-btn>
			<v-btn
				class="ml-1"
				small
				@click="crear = true"
				color="primary"
				v-if="$route.query.codigoContrato && $root.acceso('CREAR_INCIDENCIAS')"
				:disabled="abierta"
			>
				<v-icon small left>mdi-plus</v-icon>
				Crear Incidencia
			</v-btn>
		</div>

		<div class="d-flex mt-2">
			<v-alert
				text
				type="info"
				dense
				v-if="abierta && $route.query.codigoContrato && $root.acceso('CREAR_INCIDENCIAS')"
				>No puedes crear una incidencia habiendo una abierta</v-alert
			>
		</div>

		<v-card class="mt-2">
			<v-data-table
				fixed-header
				:items="incidencias"
				:headers="incidenciasHeader"
				:calculate-widths="true"
				checkbox-color="secondary"
				:loading="loading"
				:footer-props="{
					showFirstLastPage: true,
					showCurrentPage: true,
					itemsPerPageOptions: [10, 25, 50],
				}"
				show-select
				:single-select="false"
				item-key="idIncidencia"
			>
				<template v-slot:body.prepend="{ headers }">
					<table-filters
						:headers="headers"
						:items="incidencias"
						v-model="inlineFilters"
					></table-filters>
				</template>

				<template v-slot:item.codigoContrato="{ item }">
					<v-tooltip bottom>
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								v-on="on"
								v-bind="attrs"
								link
								:to="`/detalles_contrato?codigoContrato=${item.codigoContrato}&idContrato=${item.idContrato}`"
								color="secondary"
								text
								>{{ item.codigoContrato }}</v-btn
							>
						</template>
						<span>Detalles del contrato</span>
					</v-tooltip>
				</template>

				<template v-slot:item.estado="{ item }">
					<span
						v-text="item.estado"
						:class="`${
							item.estado == 'Cerrada' ? 'error--text' : 'success--text'
						}`"
					/>
				</template>

				<template v-slot:item.fecha="{ item }">
					<span v-text="parseDate(item.fecha * 1000)" />
				</template>

				<template v-slot:item.empresa="{ item }">
					<empresa-chip v-if="item.empresa" :value="item.empresa" />
				</template>

				<template v-slot:item.mensaje="{ item }">
					<span
						class="d-inline-block text-truncate"
						style="max-width: 400px; max-height: 17px"
						v-html="item.mensaje"
					/>
				</template>

				<template v-slot:item.acciones="{ item }">
					<v-btn
						color="primary"
						small
						outlined
						@click="
							idIncidencia = item.id;
							codigoContrato = item.codigoContrato;
							idContrato = item.idContrato;
						"
					>
						<v-icon left small>mdi-eye</v-icon>
						ver detalles
					</v-btn>
				</template>
			</v-data-table>
			<portal-target name="filter-btn" />
		</v-card>

		<v-dialog
			persistent
			transition="dialog-bottom-transition"
			fullscreen
			:value="idIncidencia != null"
		>
			<detalles-incidencia
				@close="
					idIncidencia = null;
					getIncidencias;
				"
				:key="idIncidencia"
				:idIncidencia="idIncidencia"
				:codigoContrato="codigoContrato"
				:idContrato="idContrato"
				@reload="
					getIncidencias();
					getContrato();
				"
			/>
		</v-dialog>

		<v-dialog
			persistent
			v-model="crear"
			max-width="800px"
			v-if="$root.acceso('CREAR_INCIDENCIAS')"
		>
			<v-card>
				<v-toolbar :elevation="0" color="primary">
					<v-toolbar-title class="white--text"
						>Crear incidencia para el contrato
						{{ $route.query.codigoContrato }}</v-toolbar-title
					>
					<v-spacer></v-spacer>
					<v-btn icon color="white" @click="crear = false">
						<v-icon>mdi-close</v-icon>
					</v-btn>
				</v-toolbar>
				<v-form @submit.prevent="crearIncidencia" class="pa-4" ref="form">
					<v-container fluid class="pa-2 mt-2">
						<h3 class="text-h6 mb-4">
							Escribe aquí el primer mensaje de la incidencia:
						</h3>
						<v-row>
							<v-col cols="12">
								<text-editor
									v-model="mensaje"
									:error="emptyMsg"
									@input="(v) => (emptyMsg = !v)"
									class="mb-2"
								/>
							</v-col>
							<v-col cols="12">
								<div class="d-flex">
									<v-spacer />
									<v-btn outlined color="primary" @click="crearIncidencia">
										<v-icon left>mdi-content-save</v-icon>
										Crear Incidencia
									</v-btn>
								</div>
							</v-col>
						</v-row>
					</v-container>
				</v-form>
			</v-card>
		</v-dialog>
	</v-container>
</template>

<script>
import {
	perColumnFilter,
	parseDate,
	decode64,
} from "@/utils/index.js";
import { req } from "@/utils/validations.js";

export default {
	components: {
		DetallesIncidencia: () => import("@/views/incidencias/DetallesIncidencia.vue"),
		TableFilters: () => import("@/components/TableFilters.vue"),
		TextEditor: () => import("@/components/TextEditor.vue"),
		StatusChip: () => import("@/components/StatusChip.vue"),
		EmpresaChip: () => import("@/components/EmpresaChip.vue"),
		Contratos: () => import("@/views/contratos/Contratos.vue"),
	},
	data() {
		return {
			idIncidencia: this.$route.query.idIncidencia || null,
			codigoContrato: null,
			idContrato: null,
			loading: true,
			crear: false,
			incidencias: [],
			mensaje: "",
			emptyMsg: false,
			rules: {
				req,
			},
			inlineFilters: {},
			incidenciasHeader: [
				{ text: "Número", value: "id" },
				{ text: "Contrato", value: "codigoContrato" },
				{ text: "Fecha", value: "fecha", dataType: "date" },
				{ text: "Motivo", value: "mensaje" },
				{ text: "Estado", value: "estado", dataType: "select" },
				{
					text: "Acciones",
					value: "acciones",
					sortable: false,
					filterable: false,
				},
			].map((header) => {
				return {
					class: "text-no-wrap sticky-header",
					cellClass: "pa-2 text-no-wrap",
					filter: (value) =>
						perColumnFilter(
							value,
							this.inlineFilters[header.value],
							header.dataType
						),
					dataType: "text",
					...header,
				};
			}),
		};
	},
	mounted() {
		this.getIncidencias();
	},
	computed: {
		abierta() {
			return this.incidencias.some((i) => i.estado == "Abierta");
		},
	},
	methods: {
		parseDate,
		decode64,
		getIncidencias() {
			let str = this.$store.getters.getDatosEmpresa;
			const { IdAgente } = str.iss;
			axios({
				method: "GET",
				url: `${process.env.VUE_APP_OUR_API_URL}/incidencias.php`,
				params: {
					token: this.$store.getters.getJwtEmpresa,
					codigoContrato: this.$route.query.codigoContrato,
					idAgente: IdAgente,
				},
			}).then(({ data }) => {
				this.incidencias = data;
				this.loading = false;
			}).catch(err => console.error(err));
		},
		async crearIncidencia() {
			if (this.mensaje.length == 0) {
				this.emptyMsg = true;
				return;
			}
			if (!this.$refs.form.validate()) return;

			let str = this.$store.getters.getDatosEmpresa;
			const { IdAgente } = str.iss;

			const { data: contratos } = await axios({
				method: "GET",
				url: `${process.env.VUE_APP_OUR_API_URL}/contratosVerificados.php`,
				params: {
					token: this.$store.getters.getJwtEmpresa,
					IdAgente,
					codigoContrato: this.$route.query.codigoContrato,
				},
			})

			axios({
				method: "POST",
				url: `${process.env.VUE_APP_OUR_API_URL}/incidencias.php`,
				data: {
					token: this.$store.getters.getJwtEmpresa,
					codigoContrato: this.$route.query.codigoContrato,
					empresa: JSON.parse(
						decode64(this.$store.getters.getJwtEmpresa.split(".")[1])
					).empresa,
					mensaje: this.mensaje,
					idAgente: contratos[0].IdAgente,
				},
			})
				.then((res) => {
					this.$root.$emit("snack", "Se ha creado la incidencia con éxito");
					this.crear = false;
					// this.getContrato();
					this.getIncidencias();
				})
				.catch((err) => {
					console.error(err);
					this.$root.$emit("snack", "Ha ocurrido un error");
				});
		},
	},
};
</script>

<style scoped>
.v-data-table /deep/ [role="columnheader"] {
	/* background: #f1f1f1 !important; */
}
</style>